import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGetTimesheetApprovals, type TimesheetApprovalRow } from "@/lib/api";
import { TimecardReviewDialog, type ReviewTarget } from "@/components/timekeeping/TimecardReviewDialog";
import { formatLocalizedDate } from "@/lib/localized-date";

export function ApprovalsPanel() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [review, setReview] = useState<ReviewTarget | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["timesheet-approvals"],
    queryFn: apiGetTimesheetApprovals,
    refetchInterval: 60_000,
  });

  const openReview = (row: TimesheetApprovalRow) => setReview({
    id: row.id, employee_id: row.employee_id, employee_name: row.employee_name, week_start_date: row.week_start_date,
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  if (rows.length === 0) {
    return (
      <Card><CardContent className="py-10 text-center text-muted-foreground">
        {t("timekeeping.approvals.empty", { defaultValue: "No timesheets awaiting your approval." })}
      </CardContent></Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="min-w-0 overflow-x-auto p-0">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-left">
                <th className="px-4 py-2 font-medium">{t("timekeeping.approvals.employee", { defaultValue: "Employee" })}</th>
                <th className="px-4 py-2 font-medium">{t("timekeeping.approvals.week", { defaultValue: "Week" })}</th>
                <th className="px-4 py-2 font-medium">{t("timekeeping.week.total", { defaultValue: "Total" })}</th>
                <th className="px-4 py-2 w-48" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const effectiveHours = Number.isFinite(Number(row.effectiveRequiredHours))
                  ? Number(row.effectiveRequiredHours)
                  : Number(row.required_hours);
                return (
                  <tr
                    key={row.id}
                    className="border-b last:border-0 cursor-pointer hover:bg-muted/30"
                    onClick={() => openReview(row)}
                  >
                    <td className="px-4 py-2">
                      <div className="font-medium">{row.employee_name}</div>
                      <div className="text-xs text-muted-foreground">{row.department || ""}</div>
                    </td>
                    <td className="px-4 py-2 tabular-nums">
                      {formatLocalizedDate(row.week_start_date, i18n.resolvedLanguage || i18n.language)}
                    </td>
                    <td className="px-4 py-2 tabular-nums">
                      <div>{Number(row.total_hours)} / {effectiveHours}h</div>
                      {effectiveHours !== Number(row.required_hours) && (
                        <div className="text-xs text-muted-foreground">
                          {t("timekeeping.week.standardBaselineShort", {
                            baseline: Number(row.required_hours),
                            defaultValue: "Standard: {{baseline}}h",
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openReview(row); }}>
                        <Eye className="h-4 w-4 mr-1" />{t("timekeeping.approvals.view", { defaultValue: "View" })}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <TimecardReviewDialog
        target={review}
        onOpenChange={(v) => { if (!v) setReview(null); }}
        onDone={() => { setReview(null); qc.invalidateQueries({ queryKey: ["timesheet-approvals"] }); }}
      />
    </>
  );
}
